import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, IconButton } from "@mui/material";
import { X } from "lucide-react";

export default function DialogManager({
    dialogs,
    currentForm,
    selectedGoal,
    categories,
    selectedDate,
    getDayTransactions,
    onClose,
    onFormChange,
    onAddCategory,
    onAddTransaction,
    onAddNote,
    onContributeToGoal,
    deleteTransaction,
    onSetSelectedDate
}) {
    const handleCloseDialog = (type) => {
        onClose(type);
    };

    const categoryName = currentForm.category.trim();
    const amount = Number(currentForm.amount);
    const limit = Number(currentForm.limit);
    const isPositiveAmount = Number.isFinite(amount) && amount > 0;
    const isPositiveLimit = Number.isFinite(limit) && limit > 0;
    const canAddCategory = Boolean(categoryName) && (
        currentForm.categoryType === 'income' || isPositiveLimit
    );
    const canAddTransaction = Boolean(currentForm.category && currentForm.date && isPositiveAmount);
    const canAddNote = Boolean(currentForm.note.trim() && currentForm.date);
    const canContributeToSelectedGoal = Boolean(selectedGoal && isPositiveAmount);
    const canContributeToGoal = Boolean(currentForm.category && isPositiveAmount);

    return (
        <>
            {/* Диалог категории */}
            <Dialog open={dialogs.category} onClose={() => handleCloseDialog('category')}>
                <DialogTitle>Добавить {currentForm.categoryType === 'goals' ? 'цель' : 'категорию'}</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={currentForm.categoryType}
                        onChange={(e) => onFormChange({ categoryType: e.target.value })}
                        className="mb-4"
                    >
                        <MenuItem value="expenses">Расходы</MenuItem>
                        <MenuItem value="income">Доходы</MenuItem>
                        <MenuItem value="goals">Цели</MenuItem>
                    </Select>
                    <TextField
                        fullWidth
                        label="Название категории"
                        value={currentForm.category}
                        onChange={(e) => onFormChange({ category: e.target.value })}
                        className="mb-4"
                    />
                    {(currentForm.categoryType === 'expenses' || currentForm.categoryType === 'goals') && (
                        <TextField
                            fullWidth
                            label={currentForm.categoryType === 'goals' ? "Целевая сумма" : "Лимит"}
                            type="number"
                            value={currentForm.limit}
                            onChange={(e) => onFormChange({ limit: e.target.value })}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseDialog('category')}>Отмена</Button>
                    <Button
                        onClick={() => onAddCategory(currentForm.categoryType)}
                        variant="contained"
                        disabled={!canAddCategory}
                    >
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог расхода */}
            <Dialog open={dialogs.expense} onClose={() => handleCloseDialog('expense')}>
                <DialogTitle>Добавить расход</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={currentForm.category}
                        onChange={(e) => onFormChange({ category: e.target.value })}
                        className="mb-4"
                    >
                        {categories.expenses.map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                        ))}
                    </Select>
                    <TextField
                        fullWidth
                        label="Сумма"
                        type="number"
                        value={currentForm.amount}
                        onChange={(e) => onFormChange({ amount: e.target.value })}
                        className="mb-4"
                    />
                    <TextField
                        fullWidth
                        type="date"
                        value={currentForm.date}
                        onChange={(e) => onFormChange({ date: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseDialog('expense')}>Отмена</Button>
                    <Button
                        onClick={() => onAddTransaction('expenses')}
                        variant="contained"
                        disabled={!canAddTransaction}
                    >
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог дохода */}
            <Dialog open={dialogs.income} onClose={() => handleCloseDialog('income')}>
                <DialogTitle>Добавить доход</DialogTitle>
                <DialogContent>
                    <Select
                        fullWidth
                        value={currentForm.category}
                        onChange={(e) => onFormChange({ category: e.target.value })}
                        className="mb-4"
                    >
                        {categories.income.map(cat => (
                            <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                        ))}
                    </Select>
                    <TextField
                        fullWidth
                        label="Сумма"
                        type="number"
                        value={currentForm.amount}
                        onChange={(e) => onFormChange({ amount: e.target.value })}
                        className="mb-4"
                    />
                    <TextField
                        fullWidth
                        type="date"
                        value={currentForm.date}
                        onChange={(e) => onFormChange({ date: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseDialog('income')}>Отмена</Button>
                    <Button
                        onClick={() => onAddTransaction('income')}
                        variant="contained"
                        disabled={!canAddTransaction}
                    >
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог заметки */}
            <Dialog open={dialogs.note} onClose={() => handleCloseDialog('note')}>
                <DialogTitle>Добавить заметку</DialogTitle>
                <DialogContent>
                    <div className="space-y-4 mt-4">
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Текст заметки"
                            value={currentForm.note}
                            onChange={(e) => onFormChange({ note: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label="Дата"
                            value={currentForm.date}
                            onChange={(e) => onFormChange({ date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            type="datetime-local"
                            label="Напоминание"
                            value={currentForm.reminder || ''}
                            onChange={(e) => onFormChange({ reminder: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                checked={currentForm.isTop}
                                onChange={(e) => onFormChange({ isTop: e.target.checked })}
                                className="mr-2"
                            />
                            <span>Сделать топ-заметкой</span>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseDialog('note')}>Отмена</Button>
                    <Button onClick={onAddNote} variant="contained" disabled={!canAddNote}>
                        Добавить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог пополнения цели */}
            <Dialog open={dialogs.goalContribution} onClose={() => handleCloseDialog('goalContribution')}>
                <DialogTitle>Пополнить цель: {selectedGoal?.name}</DialogTitle>
                <DialogContent>
                    <div className="space-y-4 mt-4">
                        <TextField
                            fullWidth
                            label="Сумма"
                            type="number"
                            value={currentForm.amount}
                            onChange={(e) => onFormChange({ amount: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label="Дата"
                            value={currentForm.date}
                            onChange={(e) => onFormChange({ date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseDialog('goalContribution')}>Отмена</Button>
                    <Button
                        onClick={() => onContributeToGoal(selectedGoal?.id)}
                        variant="contained"
                        disabled={!canContributeToSelectedGoal}
                    >
                        Пополнить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог выбора цели */}
            <Dialog open={dialogs.goal} onClose={() => handleCloseDialog('goal')}>
                <DialogTitle>Пополнить цель</DialogTitle>
                <DialogContent>
                    <div className="space-y-4 mt-4">
                        <Select
                            fullWidth
                            value={currentForm.category}
                            onChange={(e) => onFormChange({ category: e.target.value })}
                            className="mb-4"
                        >
                            {categories.goals.map(goal => (
                                <MenuItem key={goal.id} value={goal.id}>
                                    {goal.name} ({goal.current.toLocaleString()} ₽ из {goal.target.toLocaleString()} ₽)
                                </MenuItem>
                            ))}
                        </Select>
                        <TextField
                            fullWidth
                            label="Сумма"
                            type="number"
                            value={currentForm.amount}
                            onChange={(e) => onFormChange({ amount: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            type="date"
                            label="Дата"
                            value={currentForm.date}
                            onChange={(e) => onFormChange({ date: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseDialog('goal')}>Отмена</Button>
                    <Button 
                        onClick={() => {
                            onContributeToGoal(currentForm.category);
                        }} 
                        variant="contained"
                        disabled={!canContributeToGoal}
                    >
                        Пополнить
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог с транзакциями за день */}
            <Dialog 
                open={Boolean(selectedDate)} 
                onClose={() => {
                    onSetSelectedDate(null);
                    onClose('dayTransactions');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className="flex justify-between items-center">
                    Транзакции за {selectedDate}
                    <IconButton
                        onClick={() => {
                            onSetSelectedDate(null);
                            onClose('dayTransactions');
                        }}
                        size="small"
                    >
                        <X />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedDate && (
                        <>
                            <div className="mb-4">
                                <h3 className="font-semibold mb-2">Расходы:</h3>
                                {getDayTransactions(selectedDate).expenses.map(t => (
                                    <div key={t.id} className="flex items-center justify-between p-2 hover:bg-gray-800 rounded">
                                        <IconButton
                                            onClick={() => deleteTransaction('expenses', t.id)}
                                            size="small"
                                            color="error"
                                        >
                                            <X size={16} />
                                        </IconButton>
                                        <span className="flex-grow ml-2">{t.categoryName}</span>
                                        <span className="text-red-400 ml-4">{t.amount} ₽</span>
                                    </div>
                                ))}
                            </div>
                            <div>
                                <h3 className="font-semibold mb-2">Доходы:</h3>
                                {getDayTransactions(selectedDate).income.map(t => (
                                    <div key={t.id} className="flex items-center justify-between p-2 hover:bg-gray-800 rounded">
                                        <IconButton
                                            onClick={() => deleteTransaction('income', t.id)}
                                            size="small"
                                            color="error"
                                        >
                                            <X size={16} />
                                        </IconButton>
                                        <span className="flex-grow ml-2">{t.categoryName}</span>
                                        <span className="text-green-400 ml-4">{t.amount} ₽</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-700">
                                <div className="flex justify-between">
                                    <span>Всего расходов:</span>
                                    <span className="text-red-400">{getDayTransactions(selectedDate).totalExpenses} ₽</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Всего доходов:</span>
                                    <span className="text-green-400">{getDayTransactions(selectedDate).totalIncome} ₽</span>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
